// The walk a flyover follows, built from a hike's raw route pieces.
//
// A hike's geometry is a MultiLineString in all but name: it is assembled from several planned
// trails, and merging them says nothing about the direction each was drawn in or whether
// consecutive ones meet. The ordinary case from the hike editor is routes merged end-to-end, so the
// point joining route 1 and route 2 is the END of both.
//
// Concatenating the raw points — which is what the flyover did before this module existed — draws a
// straight teleport across every seam, sends the camera along it, and measures it as distance. On a
// hike merged out of six routes those teleports dominate: the line reads as a scribble of long
// straight crossings and the flyover looks like it is visiting the routes in the wrong order.
//
// So: keep the hike's own order, flip a piece when its far end is the one that meets the walk so
// far, run pieces that meet into one continuous stroke, and leave every genuine gap as a break the
// caller neither draws nor spends time on. This mirrors `Trailer::RouteStitcher` on the server
// (which the Trail Trailer video already uses) and the per-segment traversal the iOS flyover does.
//
// Pure — coordinates in, coordinates out — so every rule above is unit-testable.

/** [lng, lat] or [lng, lat, elevation]; elevation defaults to 0. */
export type Coord = number[];

/** One continuous stroke of the walk: drawn as one line, flown without interruption. */
export interface RouteStroke {
  /** [lng, lat, ele] vertices. */
  coords: Coord[];
  /** Cumulative km from the stroke's own start to each vertex. */
  cumulativeKm: number[];
  /** Distance along the whole walk at which this stroke starts. */
  startKm: number;
  /** The stroke's own length in km. */
  lengthKm: number;
}

export interface FlyoverRoute {
  strokes: RouteStroke[];
  /** Every vertex, in walking order. */
  coords: Coord[];
  /**
   * Cumulative km at each vertex, with the step ACROSS a break counting 0 — the flyover neither
   * travels nor reports the gaps, so anything measuring against this agrees with the marker.
   */
  cumulativeKm: number[];
  /** Indices into `coords` where the pen lifts (the first vertex of every stroke but the first). */
  breaks: number[];
  /** Length of the walk in km, gaps excluded. */
  totalKm: number;
}

/** Two ends this close are the same place: the seam is continuous and the stroke carries through it. */
export const JOIN_TOLERANCE_KM = 0.06;
/** A leading vertex this close to the previous one restates it rather than stepping the walk. */
const DUPLICATE_TOLERANCE_KM = 0.005;
/**
 * Past this, two pieces are not a seam at all — they are separate stretches of the hike, and which
 * of their ends sits nearer the other is geography, not evidence about how either was drawn. So a
 * disconnected piece keeps its authored direction.
 */
const PLAUSIBLE_SEAM_KM = 1.0;

const EARTH_RADIUS_KM = 6371.0088;

/** Great-circle km between two coordinates. Same radius as turf.distance. */
export function distanceKm(a: Coord, b: Coord): number {
  const lat1 = (a[1] * Math.PI) / 180;
  const lat2 = (b[1] * Math.PI) / 180;
  const dLat = lat2 - lat1;
  const dLng = ((b[0] - a[0]) * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

function coord3(c: Coord): Coord {
  return [c[0], c[1], c.length > 2 ? c[2] || 0 : 0];
}

function isCoord(c: unknown): boolean {
  return Array.isArray(c) && c.length >= 2 && typeof c[0] === "number" && typeof c[1] === "number";
}

/** True when `input` is a list of pieces rather than a single list of coordinates. */
function isSegmented(input: Coord[] | Coord[][]): input is Coord[][] {
  return Array.isArray(input) && Array.isArray(input[0]) && isCoord((input[0] as Coord[])[0]);
}

/** How close `point` comes to either end of `piece` — the nearer end answers the question. */
function endsDistanceKm(point: Coord, piece: Coord[]): number {
  return Math.min(distanceKm(point, piece[0]), distanceKm(point, piece[piece.length - 1]));
}

/**
 * Flips `piece` when its END is the end that meets `tail` — by a clear margin, so a near tie (an
 * out-and-back piece, whose two ends sit on the same spot) keeps its authored direction, and only
 * when that end is close enough for "meets" to mean anything.
 */
function orientedToFollow(piece: Coord[], tail: Coord): Coord[] {
  const toStart = distanceKm(tail, piece[0]);
  const toEnd = distanceKm(tail, piece[piece.length - 1]);
  if (toEnd + JOIN_TOLERANCE_KM >= toStart) return piece;
  if (toEnd > PLAUSIBLE_SEAM_KM) return piece;
  return piece.slice().reverse();
}

/**
 * The opening piece has nothing before it to follow, so it is oriented against the piece AFTER it:
 * if its START is the end that meets the second piece, it was drawn against the walk. Without this
 * an opening piece drawn backwards not only traverses in reverse, it leaves the wrong end as the
 * tail — so the second piece measures its gap from the far end of the first, and a seam that does
 * meet gets reported as a break.
 */
function orientedOpening(piece: Coord[], following: Coord[] | undefined): Coord[] {
  if (!following) return piece;
  const fromStart = endsDistanceKm(piece[0], following);
  const fromEnd = endsDistanceKm(piece[piece.length - 1], following);
  if (fromStart + JOIN_TOLERANCE_KM >= fromEnd) return piece;
  if (fromStart > PLAUSIBLE_SEAM_KM) return piece;
  return piece.slice().reverse();
}

function strokeOf(coords: Coord[], startKm: number): RouteStroke {
  const cumulativeKm = [0];
  for (let i = 1; i < coords.length; i++) {
    cumulativeKm.push(cumulativeKm[i - 1] + distanceKm(coords[i - 1], coords[i]));
  }
  return { coords, cumulativeKm, startKm, lengthKm: cumulativeKm[cumulativeKm.length - 1] };
}

/**
 * Builds the walk from `input`, which is either the pieces of a hike's route (a list of coordinate
 * lists) or a single already-continuous line (a recording's track), in which case the result is one
 * stroke and nothing is reordered or flipped.
 */
export function buildFlyoverRoute(input: Coord[] | Coord[][]): FlyoverRoute {
  const pieces: Coord[][] = (isSegmented(input) ? input : [input as Coord[]])
    .map(piece => (Array.isArray(piece) ? piece.filter(isCoord).map(coord3) : []))
    .filter(piece => piece.length >= 2);

  if (pieces.length === 0) {
    return { strokes: [], coords: [], cumulativeKm: [], breaks: [], totalKm: 0 };
  }

  // Orient every piece to follow the one before it, then run the pieces that meet together.
  const oriented: Coord[][] = [orientedOpening(pieces[0], pieces[1])];
  for (let i = 1; i < pieces.length; i++) {
    oriented.push(orientedToFollow(pieces[i], oriented[i - 1][oriented[i - 1].length - 1]));
  }

  const runs: Coord[][] = [];
  for (const piece of oriented) {
    const current = runs[runs.length - 1];
    if (!current) {
      runs.push(piece.slice());
      continue;
    }
    const tail = current[current.length - 1];
    const gap = distanceKm(tail, piece[0]);
    if (gap > JOIN_TOLERANCE_KM) {
      runs.push(piece.slice());
      continue;
    }
    current.push(...(gap <= DUPLICATE_TOLERANCE_KM ? piece.slice(1) : piece));
  }

  const strokes: RouteStroke[] = [];
  let startKm = 0;
  for (const run of runs) {
    if (run.length < 2) continue;
    const stroke = strokeOf(run, startKm);
    strokes.push(stroke);
    startKm += stroke.lengthKm;
  }

  const coords: Coord[] = [];
  const cumulativeKm: number[] = [];
  const breaks: number[] = [];
  for (const stroke of strokes) {
    if (coords.length > 0) breaks.push(coords.length);
    stroke.coords.forEach((c, i) => {
      coords.push(c);
      cumulativeKm.push(stroke.startKm + stroke.cumulativeKm[i]);
    });
  }

  return { strokes, coords, cumulativeKm, breaks, totalKm: startKm };
}

/** The stroke `km` into the walk falls in. Past the end, the last stroke. */
function strokeAtKm(route: FlyoverRoute, km: number): RouteStroke | undefined {
  const { strokes } = route;
  if (strokes.length === 0) return undefined;
  for (const stroke of strokes) {
    if (km <= stroke.startKm + stroke.lengthKm) return stroke;
  }
  return strokes[strokes.length - 1];
}

/** Linear interpolation between two coordinates, elevation included. */
function lerpCoord(a: Coord, b: Coord, f: number): Coord {
  return [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f, a[2] + (b[2] - a[2]) * f];
}

/** The coordinate `localKm` into `stroke`, clamped to its ends. */
function coordInStroke(stroke: RouteStroke, localKm: number): Coord {
  const { coords, cumulativeKm } = stroke;
  if (localKm <= 0) return coords[0];
  if (localKm >= stroke.lengthKm) return coords[coords.length - 1];
  let hi = 1;
  while (hi < cumulativeKm.length - 1 && cumulativeKm[hi] < localKm) hi++;
  const span = cumulativeKm[hi] - cumulativeKm[hi - 1];
  if (span <= 0) return coords[hi];
  return lerpCoord(coords[hi - 1], coords[hi], (localKm - cumulativeKm[hi - 1]) / span);
}

/**
 * Where the marker sits `km` into the walk. Never between two strokes: at the end of one stroke it
 * steps straight to the start of the next, because the ground in between is not part of the hike.
 */
export function coordAtKm(route: FlyoverRoute, km: number): Coord | undefined {
  const stroke = strokeAtKm(route, km);
  if (!stroke) return undefined;
  return coordInStroke(stroke, km - stroke.startKm);
}

/**
 * A coordinate `aheadKm` further on, for reading the direction of travel. Clamped to the end of the
 * current stroke: looking into the next one would read the bearing off the gap between them.
 */
export function lookAheadCoordAtKm(route: FlyoverRoute, km: number, aheadKm: number): Coord | undefined {
  const stroke = strokeAtKm(route, km);
  if (!stroke) return undefined;
  const local = Math.min(km - stroke.startKm + aheadKm, stroke.lengthKm);
  return coordInStroke(stroke, local);
}

/**
 * The travelled line as MultiLineString coordinates: every stroke the walk has finished, plus the
 * active stroke up to `km`. The pen lifts between strokes, so a gap is never drawn as a straight
 * line across it.
 */
export function drawnStrokesUpToKm(route: FlyoverRoute, km: number): Coord[][] {
  const drawn: Coord[][] = [];
  for (const stroke of route.strokes) {
    if (km <= stroke.startKm) break;
    const localKm = Math.min(km - stroke.startKm, stroke.lengthKm);
    if (localKm >= stroke.lengthKm) {
      drawn.push(stroke.coords);
      continue;
    }
    const { coords, cumulativeKm } = stroke;
    const partial: Coord[] = [coords[0]];
    for (let i = 1; i < coords.length && cumulativeKm[i] <= localKm; i++) partial.push(coords[i]);
    const tip = coordInStroke(stroke, localKm);
    const last = partial[partial.length - 1];
    if (tip[0] !== last[0] || tip[1] !== last[1]) partial.push(tip);
    if (partial.length >= 2) drawn.push(partial);
  }
  return drawn;
}
